<script setup lang="ts">
import {createApp, nextTick, onMounted, ref, type Ref} from 'vue';
import MapService from '@/services/Map.service';
import type {Feature} from '@/interfaces/geo/GeoMap.interface';
import EventMapService from '@/services/EventMap.service';
import MapPopup from '@/components/MapPopup.vue';

const mapService: MapService = MapService.getInstance();
const eventMapService: EventMapService = EventMapService.getInstance();
const mapElement: Ref<HTMLDivElement | null> = ref(null);
const baseContainerElement: Ref<HTMLDivElement | null> = ref(null);
const fullscreenContainerElement: Ref<HTMLDivElement | null> = ref(null);
const showFullscreenMap: Ref<boolean> = ref(false);

onMounted(() => {
  mapService.whenMapObjectSelected.subscribe((feature: Feature): void => {
    mapService.openPopup(mapPopupHtml(feature)).subscribe();
  });
  mapService.whenMapObjectUnselected.subscribe((): void => {
    mapService.closePopup().subscribe();
  });
  mapService.initMap(mapElement.value!, eventMapService.eventMap()!.locationWgs);
});

function mapPopupHtml(feature: Feature): HTMLElement {
  const popup = createApp(MapPopup, {point: eventMapService.eventPoint(feature.values_.ORIGINALID)});
  const popupContainer: HTMLDivElement = document.createElement('div');
  popup.mount(popupContainer);
  return popupContainer.firstElementChild as HTMLElement;
}

function openFullscreen(): void {
  showFullscreenMap.value = true;
  document.body.style.overflow = 'hidden';
  nextTick(() => {
    fullscreenContainerElement.value?.insertAdjacentElement('afterbegin', mapElement.value!);
  });
}

function closeFullscreen(): void {
  showFullscreenMap.value = false;
  document.body.style.overflow = 'auto';
  nextTick(() => {
    baseContainerElement.value?.appendChild(mapElement.value!);
  });
}
</script>

<template>
  <div class="map-component map-wrapper">
    <div ref="baseContainerElement" class="map-base-container">
      <div class="map" ref="mapElement"></div>
    </div>
    <button class="open-fullscreen" @click="openFullscreen()"></button>
    <Teleport to="body">
      <div v-if="showFullscreenMap" ref="fullscreenContainerElement" class="map-fullscreen-container">
        <button class="close-fullscreen" @click="closeFullscreen()"></button>
      </div>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
.map-component {
  position: relative;
  width: 100%;
  height: 100%;

  .map-base-container {
    display: flex;
    width: 100%;
    height: 100%;
  }

  .map {
    height: 100%;
    width: 100%;
  }

  > button {
    position: absolute;
    width: 40px;
    height: 40px;
    background-color: var(--surface-subtle, #96b693);
    background-repeat: no-repeat;
    background-position: center;
    background-size: 24px 24px;

    &.open-fullscreen {
      background-image: url('@/assets/open-fullscreen.svg');
      top: 10px;
      right: 15px;
    }
  }
}
</style>

<style lang="scss">
body > .map-fullscreen-container {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 3;
  width: 100%;
  height: 100%;
  background-color: var(--color-black-50, #ffffff);

  .map {
    height: 100%;
    width: 100%;
  }

  > button {
    position: absolute;
    width: 40px;
    height: 40px;
    background-color: var(--surface-subtle, #96b693);
    background-repeat: no-repeat;
    background-position: center;
    background-size: 24px 24px;

    &.close-fullscreen {
      background-image: url('@/assets/close-fullscreen.svg');
      top: 10px;
      right: 15px;
    }
  }
}
</style>
